import { PromoCodeDetail } from '../../types/promotions';
import { discountText, formatAed, formatExpiry, formatPromoDate } from './promoFormat';

const LABEL = { fontSize: '0.75rem', color: 'var(--admin-text-muted)' };
const VALUE = { fontSize: '1.25rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' as const };
const SUB = { fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' };

/** Flow D step 1 / AC-23 — slots used / N, uses reserved & redeemed / N × X, spend, full-price value. */
export function PromoSummaryCards({ promo }: { promo: PromoCodeDetail }) {
  const { stats } = promo;
  const pct = promo.maxHolders > 0 ? Math.min(100, Math.round((promo.holdersCount / promo.maxHolders) * 100)) : 0;
  const maxUses = promo.maxHolders * promo.usesPerHolder;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(13rem, 1fr))', gap: '1rem', marginBottom: '1rem' }} data-testid="promo-summary">
      <div className="admin-card" style={{ padding: '1rem' }}>
        <div style={LABEL}>Slots used</div>
        <div style={VALUE} data-testid="promo-slots">{promo.holdersCount} / {promo.maxHolders}</div>
        <div className="promo-slots-bar" role="progressbar" aria-label="Slots used" aria-valuemin={0} aria-valuemax={promo.maxHolders} aria-valuenow={promo.holdersCount}>
          <div className="promo-slots-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        {stats.pendingHolders > 0 && <div style={SUB}>{stats.pendingHolders} pending (shared, not yet claimed)</div>}
      </div>
      <div className="admin-card" style={{ padding: '1rem' }}>
        <div style={LABEL}>Uses</div>
        <div style={VALUE} data-testid="promo-uses">{stats.usesRedeemed} redeemed</div>
        <div style={SUB}>{stats.usesReserved} reserved · max {maxUses.toLocaleString('en-AE')} ({promo.maxHolders} × {promo.usesPerHolder})</div>
      </div>
      <div className="admin-card" style={{ padding: '1rem' }}>
        <div style={LABEL}>Garagat spend</div>
        <div style={VALUE} data-testid="promo-spend">{formatAed(stats.spendRedeemed)}</div>
        <div style={SUB}>{formatAed(stats.spendReserved)} reserved on unpaid bookings</div>
      </div>
      <div className="admin-card" style={{ padding: '1rem' }}>
        <div style={LABEL}>Full-price service value</div>
        <div style={VALUE} data-testid="promo-full-price">{formatAed(stats.fullPriceValue)}</div>
        <div style={SUB}>Of redeemed orders, before the discount</div>
      </div>
    </div>
  );
}

function Term({ label, value }: { label: string; value: string }) {
  return (
    <div className="admin-field">
      <div className="admin-field-label">{label}</div>
      <div className="admin-field-value">{value}</div>
    </div>
  );
}

/** The code's terms (admin-detail-grid). */
export function PromoTermsGrid({ promo }: { promo: PromoCodeDetail }) {
  return (
    <div className="admin-card" style={{ marginBottom: '1rem' }}>
      <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Terms</h3>
      <div className="admin-detail-grid">
        <Term label="Name" value={promo.name} />
        <Term label="Discount" value={discountText(promo)} />
        <Term label="Type" value={promo.discountType === 'PERCENT' ? 'Percentage' : 'Fixed AED'} />
        <Term label="Uses per customer" value={String(promo.usesPerHolder)} />
        <Term label="Number of users" value={promo.maxHolders.toLocaleString('en-AE')} />
        <Term label="Starts" value={formatPromoDate(promo.startsAt, true)} />
        <Term label="Expires" value={promo.expiresAt ? formatPromoDate(promo.expiresAt, true) : formatExpiry(null)} />
        <Term label="Share link" value={promo.shareLink} />
        <Term label="Created" value={formatPromoDate(promo.createdAt, true)} />
      </div>
      {promo.termsLocked && (
        <div style={SUB}>Terms are locked because people hold this code. Create a new code to change the terms.</div>
      )}
    </div>
  );
}
